import { memo, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { useLocation } from "react-router";
import { DownDropContainer } from "../Dropdown";
import { MenuItemDescription, MenuItemType } from "./types";

import styles from "./style.module.scss";

export type NavbarMenuProps = {
  menuList: MenuItemDescription[],
  className?: string
}

export const NavbarMenu = memo<NavbarMenuProps>((
  {
    menuList,
    className,
  }) => {

  const [ visibilityMap, setVisibilityMap ] = useState<{ [key: string]: boolean }>({});

  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleIntersection = (entries: Array<IntersectionObserverEntry>) => {
      const updatedEntries: { [key: string]: boolean } = {};

      entries.forEach((entry: IntersectionObserverEntry) => {
        const targetId = entry.target.id;
        if (targetId) {
          updatedEntries[targetId] = entry.isIntersecting;
        }
      });

      setVisibilityMap((prev: typeof visibilityMap) => ({
        ...prev,
        ...updatedEntries,
      }));
    };

    const observer = new IntersectionObserver(handleIntersection, {
      root: navRef.current,
      threshold: 1,
    });

    if (navRef.current) {
      Array.from(navRef.current?.children)
        .forEach((item: any) => {
          if (item.id) {
            observer.observe(item);
          }
        });
    }
    return () => observer.disconnect();
  }, [ menuList ]);

  const menuItems: MenuItemType[] = useMemo(() => {
    const setItemsId = (items: MenuItemDescription[]) => {
      const newItems: MenuItemType[] = [];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        newItems.push({
          ...item,
          id: item.label + i,
          items: item.items ? setItemsId(item.items) : undefined,
        });
      }
      return newItems;
    };

    return setItemsId(menuList);
  }, [ menuList ]);

  // menu items for more section
  const moreItemsData = useMemo(() => {

    return menuItems.filter((item: MenuItemType) => item?.id && !visibilityMap[item.id]);

  }, [ menuItems, visibilityMap ]);

  const [ activeMenuItem, setActiveMenuItem ] = useState<string | undefined>(undefined);

  const activeSubItem = useMemo(() => moreItemsData
    .find(item => item?.label === activeMenuItem), [ activeMenuItem, moreItemsData ]);

  const { pathname } = useLocation();

  useEffect(() => {
    const activeItem = menuList.find(item => (item?.path && pathname.includes(item.path))
      || item?.items?.find(el => el?.path && pathname.includes(el.path)));
    setActiveMenuItem(activeItem?.label);
  }, [ pathname, menuList ]);

  return (
    <ul className={classNames(styles.menuUl, className)}>
      <div className={styles.collapsingWrapper} ref={navRef}>
        {menuItems.filter(item => !item.defaultHide)
          .map((menuItem) => (
            <DownDropContainer
              key={menuItem.id}
              className={classNames({
                [styles.visible]: visibilityMap[menuItem.id!],
                [styles.inVisible]: !visibilityMap[menuItem.id!],
              })} item={menuItem} activeMenuItem={activeMenuItem === menuItem?.label} />
          ))}

        {!!moreItemsData.length && <DownDropContainer
          activeSubItem={activeSubItem?.label}
          activeMenuItem={!!activeSubItem}
          item={{ id: "more_internal", label: "More", items: moreItemsData }}
        />}
      </div>
    </ul>
  );
});

